// #489 Etappe V4: Ansicht „Vorgänge“ in den Tickets und Widget „Vorgang“ – reine Funktionen.
import { zaehleChecklistenFortschritt } from '../tickets/checklistenVorlagen';
import type { AngabeStatus } from './start';

export type GespeicherteAngabe = { bezeichnung: string; status: AngabeStatus; wert: string | null };

/** Felder eines Tickets, die die Vorgangsansicht liest (Ticket aus pages/admin/tickets/types.ts erfüllt das). */
export interface VorgangsTicketBasis {
  id: string;
  status: string;
  beschreibung: string | null;
  faellig_am: string | null;
  vorgangsart_id?: string | null;
  vorgang_angaben?: unknown;
}

const ABGESCHLOSSEN = new Set(['erledigt', 'geschlossen']);

export const istOffen = (t: Pick<VorgangsTicketBasis, 'status'>): boolean => !ABGESCHLOSSEN.has((t.status || '').toLowerCase());

/** tickets.vorgang_angaben robust lesen (Reihenfolge wie gespeichert). */
export function leseTicketAngaben(roh: unknown): (GespeicherteAngabe & { schluessel: string })[] {
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return [];
  return Object.entries(roh as Record<string, unknown>).flatMap(([schluessel, v]) => {
    if (!v || typeof v !== 'object') return [];
    const o = v as Record<string, unknown>;
    const status = (['mail', 'app', 'intern', 'fehlt'] as const).find(s => s === o.status) ?? 'fehlt';
    const bezeichnung = typeof o.bezeichnung === 'string' && o.bezeichnung.trim() ? o.bezeichnung : schluessel;
    const wert = typeof o.wert === 'string' && o.wert.trim() ? o.wert : null;
    return [{ schluessel, bezeichnung, status, wert }];
  });
}

/** Bezeichnungen der Angaben, die noch beim Absender fehlen. */
export function fehlendeAngaben(roh: unknown): string[] {
  return leseTicketAngaben(roh).filter(a => a.status === 'fehlt').map(a => a.bezeichnung);
}

/** Erster offener Checklistenpunkt (☐ …) – „nächster Schritt“ des Vorgangs. */
export function naechsterPunkt(beschreibung: string | null | undefined): string | null {
  const z = (beschreibung ?? '').split('\n').find(l => l.startsWith('☐ '));
  return z ? z.slice(2).trim() || null : null;
}

export function fortschritt(beschreibung: string | null | undefined): { erledigt: number; gesamt: number; prozent: number } {
  const f = zaehleChecklistenFortschritt(beschreibung);
  return { ...f, prozent: f.gesamt ? Math.round((f.erledigt / f.gesamt) * 100) : 0 };
}

export type FristLage = 'ueberfaellig' | 'heute' | 'bald' | 'spaeter' | null;

/** Frist relativ zu heute (YYYY-MM-DD); „bald“ = innerhalb von 3 Tagen. */
export function fristLage(faellig: string | null | undefined, heute: string): FristLage {
  const f = (faellig ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return null;
  if (f < heute) return 'ueberfaellig';
  if (f === heute) return 'heute';
  const tage = (Date.parse(`${f}T00:00:00Z`) - Date.parse(`${heute}T00:00:00Z`)) / 86_400_000;
  return tage <= 3 ? 'bald' : 'spaeter';
}

export interface VorgangsGruppe<T> { artId: string; name: string; offen: number; ueberfaellig: number; tickets: T[] }

/**
 * Offene Vorgänge je Vorgangsart, in der Reihenfolge der Vorgangsarten; Tickets je Gruppe
 * nach Frist (überfällige zuerst, ohne Frist zuletzt). Unbekannte Arten landen unter „Sonstige Vorgänge“.
 */
export function gruppiereVorgaenge<T extends VorgangsTicketBasis>(tickets: readonly T[], arten: readonly { id: string; name: string }[], heute: string): VorgangsGruppe<T>[] {
  const offen = tickets.filter(t => t.vorgangsart_id && istOffen(t));
  const reihenfolge = [...arten.map(a => a.id), '__sonstige'];
  const name = (id: string) => arten.find(a => a.id === id)?.name ?? 'Sonstige Vorgänge';
  const je = new Map<string, T[]>();
  for (const t of offen) {
    const id = arten.some(a => a.id === t.vorgangsart_id) ? t.vorgangsart_id! : '__sonstige';
    je.set(id, [...(je.get(id) ?? []), t]);
  }
  const frist = (t: T) => (t.faellig_am ?? '').slice(0, 10) || '9999-99-99';
  return reihenfolge.filter(id => je.has(id)).map(id => {
    const liste = [...je.get(id)!].sort((a, b) => frist(a).localeCompare(frist(b)));
    return { artId: id, name: name(id), offen: liste.length, ueberfaellig: liste.filter(t => fristLage(t.faellig_am, heute) === 'ueberfaellig').length, tickets: liste };
  });
}

/** Angabe von Hand setzen (Widget): Wert gesetzt → erfasst ('mail'), leer → wieder offen ('fehlt'). */
export function setzeAngabe(roh: unknown, schluessel: string, wert: string): Record<string, GespeicherteAngabe> {
  const aus: Record<string, GespeicherteAngabe> = {};
  for (const a of leseTicketAngaben(roh)) aus[a.schluessel] = { bezeichnung: a.bezeichnung, status: a.status, wert: a.wert };
  const alt = aus[schluessel];
  if (!alt) return aus;
  const w = wert.trim();
  aus[schluessel] = w ? { ...alt, status: 'mail', wert: w } : { ...alt, status: alt.status === 'app' ? 'app' : 'fehlt', wert: null };
  return aus;
}

/** Text der Rückfrage-Mail aus dem Widget („Fehlende Angaben anfragen“). */
export function rueckfrageText(ticketNummer: string | null | undefined, fragen: readonly string[]): string {
  const bezug = ticketNummer ? ` (Vorgang ${ticketNummer})` : '';
  return `Guten Tag,\n\nzu Ihrem Anliegen${bezug} benötigen wir noch folgende Angaben:\n${fragen.map(f => `- ${f}`).join('\n')}\n\nVielen Dank für Ihre Mithilfe.\n\nMit freundlichen Grüßen`;
}
